// Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Briefcase, Users, Building2 } from 'lucide-react';
import logo from '../../assets/logg.jpeg';
import './Navbar.css';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  const roleModalRef = useRef(null);

  // Public role options. `slug` = internal software role.
  const roleOptions = [
    {
      slug: 'candidate',            // internal role
      label: 'Technical Professional', // public label
      description: 'Build your profile and get discovered.',
      icon: Briefcase,
    },
    {
      slug: 'mentor',
      label: 'Mentor',
      description: 'Guide the next generation of talent.',
      icon: Users,
    },
    {
      slug: 'organization',
      label: 'Organization',
      description: 'Hire, train, and grow your teams.',
      icon: Building2,
    },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close role modal on ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsRoleModalOpen(false);
    };
    if (isRoleModalOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isRoleModalOpen]);

  // Close menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsRoleModalOpen(false);
  }, [location.pathname]);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleJoinEcosystem = () => {
    setIsMobileMenuOpen(false);
    setIsRoleModalOpen(true);
  };

  const handleRoleSelect = (role) => {
    setIsRoleModalOpen(false);
    // Navigate to join flow with the selected role.
    // `role.slug` is the internal role value (e.g. "candidate").
    navigate(`/join-ecosystem?role=${role.slug}`);
  };

  const navItems = [
    { href: '/', label: 'Platform' },
    { href: '/services', label: 'Solutions' },
    { href: '/industries', label: 'Industries' },
    { href: '/about', label: 'About' },
  ];

  const isActive = (href) => {
    if (!href) return false;
    if (href.includes('#')) {
      const [path, hash] = href.split('#');
      return location.pathname === path && location.hash === `#${hash}`;
    }
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(href);
  };

  const handleNavClick = (e, href) => {
    if (href.includes('#')) {
      const [path, hash] = href.split('#');
      if (location.pathname === path) {
        e.preventDefault();
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          window.history.pushState(null, '', href);
        }
      }
    }
  };

  return (
    <>
      <header className={`navbar-header ${scrolled ? 'navbar-scrolled' : ''}`}>
        <div className="navbar-container">
          <div className="navbar-inner">
            {/* Logo */}
            <Link to="/" className="navbar-brand">
              <img src={logo} alt="Platform Logo" className="navbar-logo" />
            </Link>

            {/* Desktop Nav */}
            <nav className="navbar-nav-desktop" aria-label="Main navigation">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`nav-link-desktop ${
                    isActive(item.href) ? 'active' : ''
                  }`}
                  onClick={(e) => handleNavClick(e, item.href)}
                >
                  {item.label}
                  <span
                    className={`nav-link-underline ${
                      isActive(item.href) ? 'active' : ''
                    }`}
                  />
                </Link>
              ))}
            </nav>

            {/* CTA Buttons — Sign In (secondary) + Join Ecosystem (primary) */}
            <div className="navbar-cta-desktop">
              <button
                onClick={handleLogin}
                className="btn-login"
                type="button"
              >
                Sign In
              </button>
              <button
                onClick={handleJoinEcosystem}
                className="btn-join"
                type="button"
              >
                Join Ecosystem
              </button>
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="navbar-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>

          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div className="navbar-mobile-menu">
              <nav className="navbar-mobile-nav" aria-label="Mobile navigation">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`nav-link-mobile ${
                      isActive(item.href) ? 'active' : ''
                    }`}
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      handleNavClick(e, item.href);
                    }}
                  >
                    {item.label}
                  </Link>
                ))}

                {/* Mobile CTAs — same order: Sign In (secondary), Join Ecosystem (primary) */}
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleLogin();
                  }}
                  className="btn-login-mobile"
                  type="button"
                >
                  Sign In
                </button>
                <button
                  onClick={handleJoinEcosystem}
                  className="btn-join-mobile"
                  type="button"
                >
                  Join Ecosystem
                </button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Role selector modal */}
      {isRoleModalOpen && (
        <div
          className="role-modal-overlay"
          onClick={(e) => {
            if (roleModalRef.current && !roleModalRef.current.contains(e.target)) {
              setIsRoleModalOpen(false);
            }
          }}
        >
          <div
            className="role-modal"
            ref={roleModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="role-modal-title"
          >
            <div className="role-modal-header">
              <div>
                <h2 id="role-modal-title" className="role-modal-title">
                  Join the Ecosystem
                </h2>
                <p className="role-modal-subtitle">
                  Choose how you'd like to get started.
                </p>
              </div>
              <button
                type="button"
                className="role-modal-close"
                onClick={() => setIsRoleModalOpen(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="role-modal-options">
              {roleOptions.map((role) => {
                const Icon = role.icon;
                return (
                  <button
                    key={role.slug}
                    type="button"
                    className="role-option"
                    onClick={() => handleRoleSelect(role)}
                  >
                    <span className="role-option-icon">
                      <Icon size={22} />
                    </span>
                    <span className="role-option-body">
                      <span className="role-option-label">{role.label}</span>
                      <span className="role-option-desc">{role.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;